// Canvas drawing shared by the grid editor and the image tracer. Grid geometry is in CSS pixels.

export const COLORS = {
  ink: '#16181d',
  line: '#d3d6dc',
  clueBg: '#f1f2f5',
  clueBgAlt: '#e9ebef',
  highlight: '#dfe7ff',
  unresolvedEmpty: '#ffc9c9',
  unresolvedFilled: '#b42318',
  clue: '#16181d',
};

const CLUE_UNIT = 0.56;

// Clue space is reserved for the most clues a line of that length can have, so it doesn't jump while drawing.
export const boardLayout = (w, h, maxWidth, maxHeight = Infinity) => {
  const rowUnits = Math.ceil(w / 2) * CLUE_UNIT + 0.5;
  const colUnits = Math.ceil(h / 2) * CLUE_UNIT + 0.5;
  const cell = Math.max(12, Math.min(40, Math.floor(Math.min(maxWidth / (w + rowUnits), maxHeight / (h + colUnits)))));
  const left = Math.round(rowUnits * cell);
  const top = Math.round(colUnits * cell);
  return { w, h, cell, left, top, width: left + w * cell + 2, height: top + h * cell + 2 };
};

export const cellAt = (layout, x, y, clamp = false) => {
  let c = Math.floor((x - layout.left) / layout.cell);
  let r = Math.floor((y - layout.top) / layout.cell);
  if (clamp) {
    c = Math.max(0, Math.min(layout.w - 1, c));
    r = Math.max(0, Math.min(layout.h - 1, r));
  } else if (c < 0 || r < 0 || c >= layout.w || r >= layout.h) return null;
  return { r, c };
};

// Sizes the canvas for the device pixel ratio and returns a context in CSS pixels.
export const prepareCanvas = (canvas, layout) => {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.round(layout.width * dpr);
  canvas.height = Math.round(layout.height * dpr);
  canvas.style.width = `${layout.width}px`;
  canvas.style.height = `${layout.height}px`;
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, layout.width, layout.height);
  return ctx;
};

export const drawClueAreas = (ctx, layout, clues, hover) => {
  const { w, h, cell, left, top } = layout;
  for (let r = 0; r < h; r++) {
    ctx.fillStyle = hover?.r === r ? COLORS.highlight : r % 2 ? COLORS.clueBgAlt : COLORS.clueBg;
    ctx.fillRect(0, top + r * cell, left, cell);
  }
  for (let c = 0; c < w; c++) {
    ctx.fillStyle = hover?.c === c ? COLORS.highlight : c % 2 ? COLORS.clueBgAlt : COLORS.clueBg;
    ctx.fillRect(left + c * cell, 0, cell, top);
  }
  if (!clues) return;
  const font = Math.max(9, Math.round(cell * 0.46));
  ctx.font = `600 ${font}px system-ui, sans-serif`;
  ctx.fillStyle = COLORS.clue;
  ctx.textBaseline = 'middle';
  const step = cell * CLUE_UNIT;
  clues.rows.forEach((runs, r) => {
    const list = runs.length ? runs : [0];
    ctx.textAlign = 'center';
    list.forEach((n, i) => {
      const x = left - cell * 0.3 - (list.length - 1 - i) * step - step / 2 + cell * 0.05;
      ctx.fillText(String(n), x, top + (r + 0.5) * cell);
    });
  });
  clues.cols.forEach((runs, c) => {
    const list = runs.length ? runs : [0];
    ctx.textAlign = 'center';
    list.forEach((n, i) => {
      const y = top - cell * 0.3 - (list.length - 1 - i) * step - step / 2 + cell * 0.1;
      ctx.fillText(String(n), left + (c + 0.5) * cell, y);
    });
  });
};

export const drawCells = (ctx, layout, cells, { unresolved, filledColor = COLORS.ink, alpha = 1 } = {}) => {
  const { w, cell, left, top } = layout;
  ctx.globalAlpha = alpha;
  cells.forEach((v, i) => {
    const x = left + (i % w) * cell, y = top + Math.floor(i / w) * cell;
    const bad = unresolved?.[i];
    if (v === 1) ctx.fillStyle = bad ? COLORS.unresolvedFilled : filledColor;
    else if (bad) ctx.fillStyle = COLORS.unresolvedEmpty;
    else return;
    ctx.fillRect(x, y, cell, cell);
  });
  ctx.globalAlpha = 1;
};

export const drawGridLines = (ctx, layout) => {
  const { w, h, cell, left, top } = layout;
  ctx.lineWidth = 1;
  for (let c = 0; c <= w; c++) {
    const x = left + c * cell + 0.5;
    ctx.strokeStyle = c % 5 === 0 ? COLORS.ink : COLORS.line;
    ctx.lineWidth = c % 5 === 0 ? 1.5 : 1;
    ctx.beginPath();
    ctx.moveTo(x, c % 5 === 0 ? 0 : top);
    ctx.lineTo(x, top + h * cell);
    ctx.stroke();
  }
  for (let r = 0; r <= h; r++) {
    const y = top + r * cell + 0.5;
    ctx.strokeStyle = r % 5 === 0 ? COLORS.ink : COLORS.line;
    ctx.lineWidth = r % 5 === 0 ? 1.5 : 1;
    ctx.beginPath();
    ctx.moveTo(r % 5 === 0 ? 0 : left, y);
    ctx.lineTo(left + w * cell, y);
    ctx.stroke();
  }
};

export const drawHover = (ctx, layout, hover) => {
  if (!hover) return;
  const { cell, left, top } = layout;
  ctx.strokeStyle = '#2f5bea';
  ctx.lineWidth = 2;
  ctx.strokeRect(left + hover.c * cell + 1, top + hover.r * cell + 1, cell - 2, cell - 2);
};

// Cells on the straight line between two cells, so a fast drag leaves no gaps.
export const cellsBetween = (a, b) => {
  const out = [];
  let { r, c } = a;
  const dr = Math.abs(b.r - r), dc = Math.abs(b.c - c);
  const sr = r < b.r ? 1 : -1, sc = c < b.c ? 1 : -1;
  let err = dc - dr;
  for (;;) {
    out.push({ r, c });
    if (r === b.r && c === b.c) return out;
    const e2 = 2 * err;
    if (e2 > -dr) { err -= dr; c += sc; }
    if (e2 < dc) { err += dc; r += sr; }
  }
};
